import type { Attributes } from "@opentelemetry/api";
import { useEffect, useRef } from "react";

import { Events, trackBeacon } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectDeviceProfile } from "features/render";
import { Platform, selectIsProcoreAndroid, selectIsProcoreIOS } from "slices/explorer";
import { telemetry } from "telemetry";

/** Keep a recent memory snapshot for the crash beacon without high-volume telemetry. */
export const MEMORY_SNAPSHOT_INTERVAL_MS = 30_000;

type RenderStatistics = {
    bufferBytes?: number;
    primitives?: number;
    textureBytes?: number;
};

type ViewWithStatistics = {
    statistics?: { render?: RenderStatistics };
};

function addFiniteAttribute(attributes: Attributes, key: string, value: unknown): void {
    if (typeof value === "number" && Number.isFinite(value)) {
        attributes[key] = value;
    }
}

function getMemorySnapshot(view: ViewWithStatistics | undefined): Attributes {
    const attributes: Attributes = {};
    const memory = (
        globalThis.performance as Performance & {
            memory?: { jsHeapSizeLimit?: number; usedJSHeapSize?: number };
        }
    ).memory;
    const render = view?.statistics?.render;

    addFiniteAttribute(attributes, "memory.js_heap_used_bytes", memory?.usedJSHeapSize);
    addFiniteAttribute(attributes, "memory.js_heap_limit_bytes", memory?.jsHeapSizeLimit);
    addFiniteAttribute(attributes, "memory.gpu_buffer_bytes", render?.bufferBytes);
    addFiniteAttribute(attributes, "memory.gpu_texture_bytes", render?.textureBytes);
    addFiniteAttribute(attributes, "render.primitives", render?.primitives);

    return attributes;
}

/**
 * Crash telemetry for the 3D viewer — reports WebGL context loss, the strongest
 * in-page precursor to a viewer crash on low-end devices. Production data so far
 * puts the large majority of these on iOS, where a heavy scene exhausts the
 * memory budget WebKit allows a single WebContent process.
 *
 * The crash is reported to two places, because they answer different questions:
 *
 * 1. Honeycomb, via {@link telemetry.beaconCrashSignal} — the engineering view.
 *    Carries the global attributes stamped on every event (`platform`, user id,
 *    `scene.id`, `company.id`/`project.id`, and the `device.*` hardware registered
 *    during GPU detection), and lands in the same dataset as the `viewer.load`
 *    span so a crash can be traced back to the session that produced it.
 * 2. Amplitude, via {@link trackBeacon} — the product view. Scoped to company and
 *    project by the analytics API, which is what makes "which customers are
 *    crashing" answerable next to the rest of the funnel.
 *
 * Both use `sendBeacon` rather than a batched/async request: the page frequently
 * dies right after context loss and anything buffered would never flush.
 * The Honeycomb signal also includes the most recent JavaScript heap and
 * render-memory snapshot when the browser exposes it. Fires on all platforms.
 */
export function useCrashTelemetry() {
    const {
        state: { canvas, view },
    } = useExplorerGlobals();
    const deviceProfile = useAppSelector(selectDeviceProfile);
    const isProcoreAndroid = useAppSelector(selectIsProcoreAndroid);
    const isProcoreIOS = useAppSelector(selectIsProcoreIOS);
    const latestMemorySnapshot = useRef<Attributes>({});

    useEffect(() => {
        const sample = () => {
            latestMemorySnapshot.current = getMemorySnapshot(view);
        };

        sample();
        const timer = setInterval(sample, MEMORY_SNAPSHOT_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [view]);

    useEffect(() => {
        if (!canvas) {
            return;
        }

        const onContextLost = (event: Event) => {
            // preventDefault keeps the context recoverable, per the WebGL spec.
            event.preventDefault();
            // `bim.crash.kind` duplicates the span name on purpose. iOS and Android
            // report viewer deaths from their own datasets under their own span names,
            // so this shared attribute is what lets one environment-wide Honeycomb
            // query count every way the viewer dies across all three platforms.
            telemetry.beaconCrashSignal("webgl_context_lost", {
                "bim.crash.kind": "webgl_context_lost",
                ...latestMemorySnapshot.current,
                ...getMemorySnapshot(view),
            });
            trackBeacon(Events.viewer.crashed, {
                reason: "webgl_context_lost",
                platform: isProcoreAndroid ? Platform.Android : isProcoreIOS ? Platform.iOS : Platform.Web,
                gpu_tier: deviceProfile.tier,
                is_mobile: deviceProfile.isMobile,
            });
        };

        canvas.addEventListener("webglcontextlost", onContextLost);

        return () => {
            canvas.removeEventListener("webglcontextlost", onContextLost);
        };
    }, [canvas, deviceProfile, isProcoreAndroid, isProcoreIOS, view]);
}
